import { inject, Injectable } from "@angular/core";
import { AlertController, ToastController } from "@ionic/angular";
import { ConfigService } from "./config.service";
import { AuthMethodType, SessionDetails, UserRoleType } from "./models/survey";
import { NavigationService } from "./navigation.service";
import { GoogleOauth } from "./oauth-plugin";
import { FetchStatus, postApi, sendApi, setUnauthorizedHandler } from "./util";
import { Capacitor } from "@capacitor/core";

@Injectable()
export class AccountService {
  toastController = inject(ToastController);
  alertController = inject(AlertController);
  config = inject(ConfigService);
  nav = inject(NavigationService);

  // How often to check if a session cookie is near expiration
  static readonly SESSION_COOKIE_CHECK_INTERVAL = 10 * 1000; // 10s

  // Maximum offset between now and when a cookie expires before it will be refreshed
  static readonly SESSION_COOKIE_REFRESH_THRESHOLD = 60 * 1000; // 60s

  // Handle for interval checking for session cookie expiration
  intervalSessionCookieTimeout?: number;

  // Email that was last used to login
  // Methods that require this are only valid with flows where an email was previously used
  email?: string;

  // Cache of the session details from localStorage
  session: SessionDetails | null = null;

  constructor() {
    void (async () => {
      this.updateSessionDetailsFromCookie();

      await this.setupIntervalSessionCookie();

      // When the API server is contacted and returns 401 Unauthorized, note that our session
      // is no longer valid.
      setUnauthorizedHandler(() => {
        this.onLoggedOut();
      });
    })();
  }

  async updateSessionDetails(sessionDetails: SessionDetails) {
    // Override this.session's properties with the new details
    const newSession = {} as SessionDetails;

    newSession.authMethod = sessionDetails.authMethod;
    newSession.expires = sessionDetails.expires;
    newSession.picture = sessionDetails.picture ?? this.session?.picture;
    newSession.role = sessionDetails.role ?? this.session?.role;

    this.session = newSession;

    if (this.isSignedIn()) {
      // Persist our valid session details
      await this.config.set("sessionDetails", JSON.stringify(this.session));
    } else {
      await this.deleteSessionDetails();
    }
  }

  async deleteSessionDetails() {
    this.session = null;

    await this.config.delete("sessionDetails");

    this.onLoggedOut();
  }

  /**
   * Gets called when it is observed that there is no longer a valid auth cookie.
   * Forces user to landing page if they are on a page that requires authorization.
   * Note - when not logged in (i.e. on the login screen), this method gets
   * called every 10 seconds, via the setupIntervalSessionCookie() method.
   */
  onLoggedOut() {

    // If the user is on a page other than landing that requires authorization,
    // force them back to the landing page. Because of how mobile window.location
    // works, this check is only really relevant for web. Do additional filtering
    // for mobile logic inside this.
    if (this.pageRequiresAuth() && window.location.pathname !== "/landing") {

      // If we are on mobile, the app does NOT initialize with the /landing path
      // but rather with '/'. Therefore check to see if the path is '/' - if so, that
      // means this app is loading up the first time. As such, we don't want to force to
      // the landing page immediately. This is because the page will always render with
      // an initial state the presents the Login/Sign Up buttons. If we are coming to
      // the app from a clicked deep link, the Login Modal will need a different state
      // which will cause different forms to render depending on where the user is coming
      // from (i.e. confirm email or reset password). If this is the case, we do NOT want
      // to force them to landing, just leave the page as it is or else those specific
      // forms will not render. This enables deep linking to work correctly.
      if (Capacitor.getPlatform() !== "web" && window.location.pathname == "/") {
        return;
      }
      else {
        NavigationService.forceTo("/landing");
      }
    }
  }

  pageRequiresAuth(): boolean {
    return location.pathname !== "/settings" && location.pathname !== "/licenses";
  }

  register(
    email: string,
    password: string,
    complete: (res: SessionDetails) => void,
    error: (err: FetchStatus) => void
  ) {
    // Cache email for saving autofill on changePassword
    this.email = email;

    void postApi<SessionDetails>(
      "/session/register",
      {
        email: email,
        password: password,
        captcha: "",
      },
      async (res: SessionDetails) => {
        await this.updateSessionDetails(res);
        complete(res);
      },
      async (err: FetchStatus) => {
        await this.deleteSessionDetails();
        error(err);
      }
    );
  }

  login(email: string, password: string, complete: (res: SessionDetails) => void, error: (err: FetchStatus) => void) {
    // Cache email for saving autofill on changePassword
    this.email = email;

    void postApi<SessionDetails>(
      "/session/login",
      {
        email: email,
        password: password,
      },
      async (res: SessionDetails) => {
        await this.updateSessionDetails(res);
        complete(res);
      },
      async (err: FetchStatus) => {
        await this.deleteSessionDetails();
        error(err);
      }
    );
  }

  async logout() {
    void GoogleOauth.logout();

    await this.deleteSessionDetails();

    void sendApi(
      "/session/logout",
      null,
      () => { },
      (_) => { }
    );
  }

  forgotPassword(email: string, complete: () => void, error: (err: FetchStatus) => void) {
    void sendApi(
      "/session/forgot_password",
      {
        email: email,
      },
      complete,
      error
    );
  }

  resetPassword(
    token: string,
    newPassword: string,
    complete: (res: SessionDetails) => void,
    error: (err: FetchStatus) => void
  ) {
    void postApi<SessionDetails>(
      "/session/reset_password",
      {
        token: token,
        newPassword: newPassword,
      },
      async (res: SessionDetails) => {
        await this.updateSessionDetails(res);
        complete(res);
      },
      async (err: FetchStatus) => {
        await this.deleteSessionDetails();
        error(err);
      }
    );
  }

  verifyEmail(token: string, complete: () => void, error: (err: FetchStatus) => void) {
    void sendApi(
      "/session/verify_email",
      {
        token: token,
      },
      complete,
      error
    );
  }

  changePassword(old_password: string, password: string, complete: () => void, error: (err: FetchStatus) => void) {
    void sendApi(
      "/account/change_password",
      {
        oldPassword: old_password,
        newPassword: password,
      },
      complete,
      error
    );
  }

  /**
   * Gets the sessionDetails from local storage and sets the session
   * varaible to this value. If there is no session in local storage, it
   * will set the session variable to null.
   */
  updateSessionDetailsFromCookie(): void {
    const cookieStr = this.config.get<string>("sessionDetails");

    if (cookieStr === null) {
      this.session = null;
      return;
    }

    if (cookieStr === "" || cookieStr === "{}") {
      this.session = null;
      return;
    }

    const details = JSON.parse(cookieStr) as SessionDetails;

    // Confirm existence of mandatory property
    if (!Object.prototype.hasOwnProperty.call(details, "expires")) {
      this.session = null;
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(details, "authMethod")) {
      details.authMethod = AuthMethodType.Unknown;
    }

    this.session = details;

    return;
  }

  getSessionCookieExpiration(): number {
    if (this.session === null) {
      return 0;
    }

    const expires: number = Date.parse(this.session.expires);
    return expires;
  }

  async renewSessionCookie() {
    await postApi<SessionDetails>(
      "/session/renew",
      {},
      async (res: SessionDetails) => {
        await this.updateSessionDetails(res);
      },
      async (_err: FetchStatus) => {
        await this.deleteSessionDetails();
      }
    );
  }

  async intervalSessionCookie() {
    const expires = this.getSessionCookieExpiration();

    if (+expires == 0) {
      await this.deleteSessionDetails();

      return;
    }

    if (+expires - Date.now() < AccountService.SESSION_COOKIE_REFRESH_THRESHOLD) {
      await this.renewSessionCookie();
    }
  }

  async setupIntervalSessionCookie() {
    if (this.intervalSessionCookieTimeout != null) {
      return;
    }

    console.log("Setting up automatic session cookie refresh");

    // Instant refresh if we're close to the deadline
    // Check expiration of cookie. If there is no cookie, delete
    // session details.
    await this.intervalSessionCookie();

    // Calls the intervalSessionCookie() every 10 seconds - which
    // checks for the session expiration. If it determines there is no cookie,
    // or if it is expired, it calls the deleteSessionDetails method,
    // which calls logout, which forces the user to navigate back to landing page.
    this.intervalSessionCookieTimeout = setInterval(
      this.intervalSessionCookie.bind(this),
      AccountService.SESSION_COOKIE_CHECK_INTERVAL
    ) as unknown as number;
  }

  isSignedIn(): boolean {
    const expr = this.getSessionCookieExpiration();

    return expr > Date.now();
  }

  async goHome() {
    const alreadyHome = ["/home", "/navigator/home", "/admin/home"].includes(window.location.pathname);

    if (this.session?.role === UserRoleType.Navigator) {
      await this.nav.to("/navigator/home", alreadyHome);
    } else if (this.session?.role === UserRoleType.Admin) {
      await this.nav.to("/admin/home", alreadyHome);
    } else {
      await this.nav.to("/home", alreadyHome);
    }
  }

  async toast(message: string, duration = 5000) {
    const toast = await this.toastController.create({
      message: message,
      duration: duration,
      position: "bottom",
    });
    await toast.present();
  }

  // Show an alert to the user that must be dismissed, with a title and body
  async alert(title: string, body: string) {
    const alert = await this.alertController.create({
      header: title,
      message: body,
      buttons: ["Ok"],
    });
    await alert.present();

    // Wait for the user to clear the alert
    await alert.onDidDismiss();
  }
}
